import type { DocumentPageSchema } from '@avisoma/shared';
import YAML from 'yaml';
import StatusIndicator from '../../components/StatusIndicator';
import AuthGuard from '../../components/AuthGuard';
import { ActionableButton } from '../../components/ActionableButton';
import { DocumentClassificationDetails } from '../../components/DocumentClassificationDetails';
import { PipelineStageTiming } from '../../components/PipelineStageTiming';
import { ExtractAsMenu } from './ExtractAsMenu';
import { SegmentPreviews } from './SegmentPreviews';
import { canRetrySegmentation } from '../../lib/canRetrySegmentation';
import { pagePrimaryExtractionSkipped } from '../../lib/primaryExtractionSkipped';
import { PrimaryExtractionSkippedNotice } from '../../components/PrimaryExtractionSkippedNotice';
import { HtmlConversionRow } from './HtmlConversionRow';
import { SheetRenditionRow } from './SheetRenditionRow';
import { SupervisorRow } from './SupervisorRow';
import backend from '../../services/backend';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';

interface ProcessingStatusCardProps {
  page: DocumentPageSchema;
  // Forwarded to the inline segment previews: hovering a segment thumbnail
  // reports its bounds so the page preview can highlight the cropped region.
  onHoverSection?: (bounds: DocumentPageSchema['sectionBounds']) => void;
}

export function ProcessingStatusCard({ page, onHoverSection }: ProcessingStatusCardProps) {
  const queryClient = useQueryClient();
  const { hasAccess } = useAuth();
  // Same roles as the reconstructed-document list on DocumentPageView. A user
  // who cannot see that list has no anchor to scroll to.
  const reconstructedListVisible = hasAccess(['admin', 'supervisor', 'curator']);

  // Hide the downstream stages (Parser…Data Extraction) while segmentation is
  // still running or has forked: `processing` (the outcome isn't known yet — and
  // a Parser retry would race the in-flight fork) and `segmented` (a terminal
  // parent, swapped out of the page set; its child segment-pages carry the
  // pipeline). Only a resolved pass-through (`done`/`skip`) or a failed
  // segmentation (`error`) continues downstream on THIS page. Otherwise the rows
  // show perpetual "pending" dots and offer Retry actions that re-enqueue work on
  // a page the pipeline isn't running — so show only the Segmentation row.
  const segStatus = page.processing?.segmentation?.status;
  const segmentationBlocksDownstream = segStatus === 'processing' || segStatus === 'segmented';

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden">
      <div className="px-3 py-3 sm:px-4 sm:py-4 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-base font-medium text-gray-900 dark:text-zinc-100">Processing Status</h2>
      </div>
      <div className="px-3 py-3 sm:px-4 sm:py-4 space-y-3">
        {/* Segmentation — only when the page actually ran it. The stage is absent
            when ENABLE_SECTION_SEGMENTATION is off, so don't render a perpetual
            "pending" row for a step that never applied to this page. */}
        {page.processing?.segmentation && (
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2 min-w-0">
                <StatusIndicator status={page.processing.segmentation.status} />
                <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                  <span>Segmentation</span>
                  <PipelineStageTiming stage={page.processing.segmentation} />
                </span>
              </span>
              {canRetrySegmentation(page.processing.segmentation) && (
                <AuthGuard roles={['admin', 'curator']}>
                  <ActionableButton
                    buttonClassName="sm:py-0.5"
                    plain
                    onClick={async () => {
                      const r = await backend.pageRetry(page.id, 'segmentation');
                      queryClient.setQueryData(['documentPage', page.id], r.data);
                    }}
                  >
                    Retry
                  </ActionableButton>
                </AuthGuard>
              )}
            </dt>
            <dd>
              {page.processing.segmentation.errorMessage && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {page.processing.segmentation.errorMessage}
                </p>
              )}
              {page.processing.segmentation.status === 'segmented' && (
                <>
                  <p className="mt-1 text-xs text-violet-700 dark:text-violet-300">
                    Split into{' '}
                    {page.processing.segmentation.result?.segmentCount ??
                      page.processing.segmentation.result?.segmentPageIds?.length ??
                      0}{' '}
                    section
                    {(page.processing.segmentation.result?.segmentCount ??
                      page.processing.segmentation.result?.segmentPageIds?.length ??
                      0) === 1
                      ? ''
                      : 's'}
                    <span className="text-gray-500 dark:text-zinc-400"> — each preview links to its own page</span>
                  </p>
                  <SegmentPreviews page={page} onHoverSection={onHoverSection} />
                </>
              )}
            </dd>
          </div>
        )}

        {!segmentationBlocksDownstream && (
          <>
            {/* PDF Parser */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2 min-w-0">
                  <StatusIndicator status={page.processing?.pdfParser?.status} />
                  <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                    <span>Parser</span>
                    <PipelineStageTiming stage={page.processing?.pdfParser} />
                  </span>
                </span>
                <AuthGuard roles={['admin', 'curator']}>
                  <ActionableButton
                    buttonClassName="sm:py-0.5"
                    plain
                    onClick={async () => {
                      const r = await backend.pageRetry(page.id, 'pdf-parser');
                      queryClient.setQueryData(['documentPage', page.id], r.data);
                    }}
                  >
                    Retry
                  </ActionableButton>
                </AuthGuard>
              </dt>
              <dd>
                {page.processing?.pdfParser?.errorMessage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {page.processing.pdfParser.errorMessage}
                  </p>
                )}
                {page.processing?.pdfParser?.result && (
                  <div className="mt-3 min-w-0">
                    <div className="max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50 p-4 dark:border-zinc-700 dark:from-zinc-900/20 dark:to-zinc-800/20">
                      {page.processing.pdfParser.result.classificationData?.title && (
                        <h6 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                          {page.processing.pdfParser.result.classificationData.title}
                        </h6>
                      )}
                      {page.processing.pdfParser.result.classificationData?.description && (
                        <p className="text-sm text-gray-900 dark:text-zinc-100">
                          {page.processing.pdfParser.result.classificationData.description}
                        </p>
                      )}
                      {page.processing.pdfParser.result.markdown?.trim() && (
                        <>
                          <hr className="my-3 border-gray-200 dark:border-zinc-700" />
                          <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-gray-900 dark:text-zinc-100">
                            {page.processing.pdfParser.result.markdown}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </dd>
            </div>

            {/* Metadata */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2">
                <StatusIndicator status={page.processing?.metadata?.status} />
                <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                  <span>Metadata</span>
                  <PipelineStageTiming stage={page.processing?.metadata} />
                </span>
              </dt>
              <dd>
                {page.processing?.metadata?.errorMessage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{page.processing.metadata.errorMessage}</p>
                )}
                {!!page.processing?.metadata?.result && (
                  <div className="mt-3 min-w-0">
                    <div className="max-w-full overflow-x-auto rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4 dark:border-green-800 dark:from-green-900/10 dark:to-emerald-900/10">
                      <pre className="whitespace-pre text-xs font-mono text-gray-900 dark:text-zinc-100">
                        {YAML.stringify(page.processing.metadata.result)}
                      </pre>
                    </div>
                  </div>
                )}
              </dd>
            </div>

            {/* Embeddings */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2">
                <StatusIndicator status={page.processing?.embeddings?.status} />
                <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                  <span>Embeddings</span>
                  <PipelineStageTiming stage={page.processing?.embeddings} />
                </span>
              </dt>
              <dd>
                {page.processing?.embeddings?.errorMessage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {page.processing.embeddings.errorMessage}
                  </p>
                )}
              </dd>
            </div>

            {/* Classification */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2 min-w-0">
                  <StatusIndicator status={page.processing?.classification?.status} />
                  <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                    <span>Classification</span>
                    <PipelineStageTiming stage={page.processing?.classification} />
                  </span>
                </span>
                <AuthGuard roles={['admin', 'curator']}>
                  <ActionableButton
                    buttonClassName="sm:py-0.5"
                    plain
                    onClick={async () => {
                      const r = await backend.pageRetry(page.id, 'classification');
                      queryClient.setQueryData(['documentPage', page.id], r.data);
                    }}
                  >
                    Retry
                  </ActionableButton>
                </AuthGuard>
              </dt>
              <dd>
                {page.processing?.classification?.errorMessage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {page.processing.classification.errorMessage}
                  </p>
                )}
                {page.processing?.classification?.result && (
                  <div className="mt-3">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <DocumentClassificationDetails classification={page.processing.classification.result} scope="page" />
                    </div>
                  </div>
                )}
              </dd>
            </div>

            <HtmlConversionRow page={page} />
            <SheetRenditionRow page={page} />

            {/* Data Extraction */}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2 min-w-0">
                  <StatusIndicator status={page.processing?.dataExtraction?.status} />
                  <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                    <span>Data Extraction</span>
                    <PipelineStageTiming stage={page.processing?.dataExtraction} />
                  </span>
                </span>
                <AuthGuard roles={['admin', 'curator']}>
                  <div className="flex items-center gap-2">
                    <ExtractAsMenu
                      currentCategory={page.processing?.dataExtraction?.overrideCategory ?? null}
                      hasResult={Boolean(page.processing?.dataExtraction?.result)}
                      onPick={async (category) => {
                        const r = await backend.extractPageAs(page.id, category);
                        queryClient.setQueryData(['documentPage', page.id], r.data);
                      }}
                    />
                    <ActionableButton
                      buttonClassName="sm:py-0.5"
                      plain
                      onClick={async () => {
                        const r = await backend.pageRetry(page.id, 'data-extraction');
                        queryClient.setQueryData(['documentPage', page.id], r.data);
                      }}
                    >
                      Retry
                    </ActionableButton>
                  </div>
                </AuthGuard>
              </dt>
              <dd>
                {page.processing?.dataExtraction?.overrideCategory && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300 italic">
                    Manually triggered as{' '}
                    <span className="font-medium">{page.processing.dataExtraction.overrideCategory}</span>
                    {page.processing.dataExtraction.overrideBy && <> by {page.processing.dataExtraction.overrideBy}</>}
                    {page.processing.dataExtraction.overrideAt && (
                      <> on {new Date(page.processing.dataExtraction.overrideAt).toLocaleString()}</>
                    )}
                    .
                  </p>
                )}
                {pagePrimaryExtractionSkipped(page.processing?.dataExtraction) && (
                  <PrimaryExtractionSkippedNotice
                    hash={reconstructedListVisible ? 'page-reconstructed-documents' : undefined}
                    category={page.processing?.dataExtraction?.category}
                  />
                )}
                {page.processing?.dataExtraction?.errorMessage && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {page.processing.dataExtraction.errorMessage}
                  </p>
                )}
                {page.processing?.dataExtraction?.result && (
                  <div className="mt-3">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      {Object.keys(page.processing.dataExtraction.result).join(', ')}
                    </div>
                  </div>
                )}
              </dd>
            </div>

            <SupervisorRow page={page} />
          </>
        )}
      </div>
    </div>
  );
}
